import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { createClient } from "@supabase/supabase-js";
import {
  getAuthFromCookie,
  clearAuthCookies,
  initializeAuth,
  setAuthCookie,
} from "../../../auth/utilities/cookieAuth";
import { syncToursFromProfile } from "../composables/useTour";

// Analytics
import { analytics, identifyUser, trackEvent, getAttribution } from "@onenterframe/analytics";

// Convert ISO date to Unix timestamp (seconds) for Customer.io
const toUnixTimestamp = (date: string | undefined): number | undefined => {
  if (!date) return undefined;
  return Math.floor(new Date(date).getTime() / 1000);
};

interface User {
  id: string;
  email: string;
  display_name?: string;
  avatar_url?: string;
  stripe_customer_id?: string;
  organization_id?: string;
  active_organization_id?: string;
  role?: string;
  metadata?: Record<string, any>;
}

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<User | null>(null);
  const accessToken = ref<string | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const isInitialized = ref(false);

  // Supabase client
  const supabase = createClient(
    (import.meta as any).env.VITE_SUPABASE_URL,
    (import.meta as any).env.VITE_SUPABASE_ANON_KEY
  );

  // Computed
  const isAuthenticated = computed(() => !!user.value && !!accessToken.value);
  const isAdmin = computed(() => user.value?.role === 'superadmin');

  // Actions
  async function initialize() {
    isLoading.value = true;
    error.value = null;

    try {
      // Try to get auth from cookie first
      const cookieAuth = getAuthFromCookie();

      if (cookieAuth.access_token) {
        accessToken.value = cookieAuth.access_token;

        // Initialize auth with Supabase
        const session = await initializeAuth(supabase);

        if (session?.user) {
          // Get user profile
          const { data: profile, error: profileError } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", session.user.id)
            .single();

          if (profileError) {
            console.error("Error fetching profile:", profileError);
          }

          user.value = {
            id: session.user.id,
            email: session.user.email!,
            display_name: profile?.display_name,
            avatar_url: profile?.avatar_url,
            stripe_customer_id: profile?.stripe_customer_id,
            organization_id: profile?.organization_id,
            active_organization_id: profile?.active_organization_id || profile?.organization_id,
            role: profile?.role,
            metadata: profile?.metadata || {},
          };

          // Sync tour completion state from server to localStorage
          syncToursFromProfile(session.user.id, profile?.metadata);

          // Identify user in Customer.io with attribution data
          identifyUser(session.user.id, {
            email: session.user.email!,
            name: profile?.display_name,
            organization_id: profile?.organization_id,
            role: profile?.role,
            created_at: session.user.created_at,
          }, 'sparks');
        }
      }
    } catch (err) {
      console.error("Auth initialization error:", err);
      error.value =
        err instanceof Error ? err.message : "Authentication failed";
      clearAuthCookies();
    } finally {
      isLoading.value = false;
      isInitialized.value = true;
    }
  }

  async function login(email: string, password: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const { data, error: loginError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      console.log('[AUTH] Supabase login response:', {
        hasData: !!data,
        hasSession: !!data?.session,
        hasUser: !!data?.session?.user,
        userId: data?.session?.user?.id,
        loginError: loginError?.message
      });

      if (loginError) {
        console.error('[ERROR] Login error:', loginError);
        error.value = loginError.message;
        return { success: false, error: loginError.message };
      }

      if (data.session) {
        accessToken.value = data.session.access_token;
        
        console.log('[AUTH] Login session data:', {
          hasSession: !!data.session,
          hasUser: !!data.session.user,
          userId: data.session.user?.id,
          userEmail: data.session.user?.email
        });

        // Set auth cookies with proper cross-domain settings
        if (data.session && data.session.user && data.session.user.id) {
          console.log('[AUTH] Setting cross-domain ha_token cookie for sparks login...');
          
          try {
            // Pass the full session object that setAuthCookie expects
            setAuthCookie(data.session);
            console.log('[OK] Cross-domain ha_token cookie set successfully');
          } catch (cookieError) {
            console.error('[ERROR] Failed to set auth cookie:', cookieError);
          }
        } else {
          console.warn('[WARN] Cannot set auth cookie - missing session or user data');
        }

        // Get user profile only if we have user data
        if (data.session.user && data.session.user.id) {
          const { data: profile, error: profileError } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", data.session.user.id)
            .single();

          if (profileError) {
            console.error("Error fetching profile:", profileError);
          }

          user.value = {
            id: data.session.user.id,
            email: data.session.user.email!,
            display_name: profile?.display_name,
            avatar_url: profile?.avatar_url,
            stripe_customer_id: profile?.stripe_customer_id,
            organization_id: profile?.organization_id,
            active_organization_id: profile?.active_organization_id || profile?.organization_id,
            role: profile?.role,
            metadata: profile?.metadata || {},
          };

          // Sync tour completion state from server to localStorage
          syncToursFromProfile(data.session.user.id, profile?.metadata);

          // Identify user in Customer.io with attribution
          identifyUser(data.session.user.id, {
            email: data.session.user.email!,
            name: profile?.display_name,
            organization_id: profile?.organization_id,
            role: profile?.role,
            plan: profile?.subscription_plan,
            credits_balance: profile?.credits_balance,
            created_at: data.session.user.created_at,
          }, 'sparks');
          
          // Track sign in event
          trackEvent.signedIn('password');
        } else {
          console.error('[ERROR] Cannot fetch user profile - session user data is missing');
          return { success: false, error: 'User session data is incomplete' };
        }

        return { success: true };
      }

      return { success: false, error: "No session returned" };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Login failed";
      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      isLoading.value = false;
    }
  }

  async function loginWithGoogle() {
    isLoading.value = true;
    error.value = null;

    try {
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (oauthError) {
        error.value = oauthError.message;
        return { success: false, error: oauthError.message };
      }

      // OAuth redirects, so we don't need to handle the response here
      // The callback will handle identification
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Google login failed";
      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      isLoading.value = false;
    }
  }

  async function signup(email: string, password: string, displayName?: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const { data, error: signupError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (signupError) {
        error.value = signupError.message;
        return { success: false, error: signupError.message };
      }

      if (data.session) {
        accessToken.value = data.session.access_token;
        setAuthCookie(data.session as any);

        user.value = {
          id: data.session.user.id,
          email: data.session.user.email!,
          display_name: displayName,
        };

        // Identify new user in Customer.io with full attribution
        const attribution = getAttribution();
        identifyUser(data.session.user.id, {
          email: data.session.user.email!,
          name: displayName,
          created_at: new Date().toISOString(),
        }, 'sparks');
        
        // Track signup with attribution (first_app, utm params, etc. included automatically)
        trackEvent.signedUp('password');

        return { success: true };
      }

      // Email confirmation required
      return {
        success: true,
        message: "Please check your email for confirmation link",
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Signup failed";
      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    isLoading.value = true;

    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      user.value = null;
      accessToken.value = null;
      clearAuthCookies();
      trackEvent.signedOut(); // Track signout and reset Customer.io user
      isLoading.value = false;
    }
  }

  async function updateProfile(updates: Partial<User>) {
    if (!user.value) return { success: false, error: "Not authenticated" };

    isLoading.value = true;
    error.value = null;

    try {
      const { error: updateError } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user.value.id);

      if (updateError) {
        error.value = updateError.message;
        return { success: false, error: updateError.message };
      }

      // Update local user state
      user.value = { ...user.value, ...updates };
      
      // Update Customer.io profile
      analytics.update(updates);

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Update failed";
      error.value = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      isLoading.value = false;
    }
  }

  return {
    // State
    user,
    accessToken,
    isLoading,
    error,
    isInitialized,

    // Computed
    isAuthenticated,
    isAdmin,

    // Actions
    initialize,
    login,
    loginWithGoogle,
    signup,
    logout,
    updateProfile,
  };
});
